"use client"

import { useInfiniteQuery } from "@tanstack/react-query"
import { useEffect, useRef } from "react"
import { cn } from "@/utilities"
import { ProductCard } from "~/components/shared/product-card"
import { ProductCardSkeleton } from "~/components/shared/product-card/skeleton"
import {
  getStoreProductsInfiniteQueryOptions,
  type StoreProductsPageLoader,
  type StoreProductsQuery,
} from "./query"

interface StoreProductsListProps {
  onLoadMoreProducts: StoreProductsPageLoader
  query: StoreProductsQuery
  queryKeySeed: string
}

const LOAD_MORE_ROOT_MARGIN = "400px 0px"
const SKELETON_COUNT = 6

export const StoreProductsList = ({
  onLoadMoreProducts,
  query,
  queryKeySeed,
}: StoreProductsListProps): React.ReactNode => {
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isPending,
    isError,
  } = useInfiniteQuery({
    ...getStoreProductsInfiniteQueryOptions({
      onLoadMoreProducts,
      query,
      queryKeySeed,
    }),
    placeholderData: (previousData) => previousData,
  })

  useEffect(() => {
    if (!(loadMoreRef.current && hasNextPage)) {
      return
    }

    let isCancelled = false
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isCancelled || !entry?.isIntersecting || isFetchingNextPage) {
          return
        }

        fetchNextPage()
      },
      { rootMargin: LOAD_MORE_ROOT_MARGIN }
    )

    observer.observe(loadMoreRef.current)

    return () => {
      isCancelled = true
      observer.disconnect()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const products = data?.pages.flatMap((page) => page.items) ?? []
  const totalProducts = data?.pages[0]?.totalProducts ?? 0
  const isRefreshing =
    isFetching && !isPending && products.length > 0 && !isFetchingNextPage

  if (isError) {
    return (
      <div
        className='rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-900 text-sm'
        role='alert'
      >
        Não foi possível carregar os produtos agora. Tente novamente mais tarde.
      </div>
    )
  }

  if (isPending) {
    return (
      <section aria-busy='true' aria-live='polite' className='space-y-4'>
        <output aria-atomic='true' aria-live='polite' className='sr-only'>
          Carregando produtos...
        </output>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3'>
          {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
            <ProductCardSkeleton
              className='w-full'
              // biome-ignore lint/suspicious/noArrayIndexKey: Static placeholder list while loading.
              key={`store-products-initial-skeleton-${index}`}
            />
          ))}
        </div>
      </section>
    )
  }

  const hasProducts = products.length > 0

  if (!hasProducts) {
    return (
      <div className='rounded-xl border border-gray-300 border-dashed bg-white p-8 text-center text-gray-600 text-sm'>
        Nenhum produto encontrado para esta loja.
      </div>
    )
  }

  return (
    <section
      aria-busy={isPending || isFetching}
      aria-live='polite'
      className='space-y-4'
    >
      <p className='text-gray-600 text-sm'>
        {totalProducts} produtos encontrados
      </p>

      <output aria-atomic='true' aria-live='polite' className='sr-only'>
        {isFetchingNextPage && "Carregando mais produtos"}
        {isRefreshing && "Atualizando produtos"}
      </output>

      {hasProducts ? (
        <div
          className={cn(
            "grid grid-cols-1 gap-4 [contain-intrinsic-size:1200px] [content-visibility:auto] sm:grid-cols-2 xl:grid-cols-3"
          )}
        >
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}

          {isFetchingNextPage &&
            Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <ProductCardSkeleton
                className='w-full'
                // biome-ignore lint/suspicious/noArrayIndexKey: Static placeholder list while loading.
                key={`store-products-next-skeleton-${index}`}
              />
            ))}
        </div>
      ) : null}

      {products.length > 0 && hasNextPage && (
        <div className='flex flex-col items-center gap-3'>
          <div aria-hidden='true' className='h-2 w-full' ref={loadMoreRef} />
        </div>
      )}
    </section>
  )
}
